import React, { useState, useEffect, useRef, useCallback } from "react";

const GRID_SIZE = 20;
const CELL_SIZE = 16;
const TICK_MS = 130;

interface LeaderboardEntry {
  user_email: string;
  best_score: number;
  achieved_at: string;
}

interface LeaderboardResponse {
  top: LeaderboardEntry[];
  personalBest: number;
  currentUserEmail: string | null;
}

function displayName(email: string): string {
  const at = email.indexOf("@");
  return at > 0 ? email.slice(0, at) : email;
}

type Point = { x: number; y: number };
type Direction = "up" | "down" | "left" | "right";

const opposite: Record<Direction, Direction> = {
  up: "down",
  down: "up",
  left: "right",
  right: "left",
};

const initialSnake: Point[] = [
  { x: 10, y: 10 },
  { x: 9, y: 10 },
  { x: 8, y: 10 },
];

function randomFood(snake: Point[]): Point {
  while (true) {
    const p = {
      x: Math.floor(Math.random() * GRID_SIZE),
      y: Math.floor(Math.random() * GRID_SIZE),
    };
    if (!snake.some((s) => s.x === p.x && s.y === p.y)) return p;
  }
}

export function SnakeGame() {
  const [snake, setSnake] = useState<Point[]>(initialSnake);
  const [food, setFood] = useState<Point>(() => randomFood(initialSnake));
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [leaderboard, setLeaderboard] = useState<LeaderboardResponse | null>(null);
  const submittedScoreRef = useRef<number | null>(null);
  const scoreRef = useRef(0);
  const directionRef = useRef<Direction>("right");
  const pendingDirectionRef = useRef<Direction>("right");

  const fetchLeaderboard = useCallback(async () => {
    try {
      const res = await fetch("/api/snake/leaderboard");
      if (!res.ok) return;
      const data = (await res.json()) as LeaderboardResponse;
      setLeaderboard(data);
    } catch {
      // Best-effort; leaderboard stays as-is on error.
    }
  }, []);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  // Keep a ref in sync with the latest score so the unmount cleanup can read it.
  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  // If the game is unmounted mid-play (e.g. loading finished while the user
  // is still playing), record whatever score they had reached.
  // `keepalive` lets the request complete even if the page is navigating away.
  useEffect(() => {
    return () => {
      const finalScore = scoreRef.current;
      if (finalScore > 0 && submittedScoreRef.current !== finalScore) {
        try {
          fetch("/api/snake/score", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ score: finalScore }),
            keepalive: true,
          });
        } catch {
          // Best-effort; the score isn't recorded if the fetch can't be queued.
        }
      }
    };
  }, []);

  const reset = useCallback(() => {
    setSnake(initialSnake);
    setFood(randomFood(initialSnake));
    setScore(0);
    setGameOver(false);
    submittedScoreRef.current = null;
    directionRef.current = "right";
    pendingDirectionRef.current = "right";
  }, []);

  useEffect(() => {
    const map: Record<string, Direction> = {
      ArrowUp: "up",
      ArrowDown: "down",
      ArrowLeft: "left",
      ArrowRight: "right",
      w: "up",
      s: "down",
      a: "left",
      d: "right",
      W: "up",
      S: "down",
      A: "left",
      D: "right",
    };
    const handleKey = (e: KeyboardEvent) => {
      const d = map[e.key];
      if (!d) return;
      e.preventDefault();
      pendingDirectionRef.current = d;
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  useEffect(() => {
    if (!gameOver || score <= 0) return;
    if (submittedScoreRef.current === score) return;
    submittedScoreRef.current = score;
    (async () => {
      try {
        await fetch("/api/snake/score", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ score }),
        });
        await fetchLeaderboard();
      } catch {
        // Best-effort; user's high score may not record on a transient failure.
      }
    })();
  }, [gameOver, score, fetchLeaderboard]);

  useEffect(() => {
    if (gameOver) return;
    const interval = setInterval(() => {
      setSnake((prev) => {
        const queued = pendingDirectionRef.current;
        if (opposite[queued] !== directionRef.current) {
          directionRef.current = queued;
        }
        const dir = directionRef.current;
        const head = prev[0]!;
        const newHead = {
          x: head.x + (dir === "right" ? 1 : dir === "left" ? -1 : 0),
          y: head.y + (dir === "down" ? 1 : dir === "up" ? -1 : 0),
        };
        if (
          newHead.x < 0 ||
          newHead.x >= GRID_SIZE ||
          newHead.y < 0 ||
          newHead.y >= GRID_SIZE
        ) {
          setGameOver(true);
          return prev;
        }
        if (prev.some((s) => s.x === newHead.x && s.y === newHead.y)) {
          setGameOver(true);
          return prev;
        }
        const ate = newHead.x === food.x && newHead.y === food.y;
        const next = ate ? [newHead, ...prev] : [newHead, ...prev.slice(0, -1)];
        if (ate) {
          setScore((s) => s + 1);
          setFood(randomFood(next));
        }
        return next;
      });
    }, TICK_MS);
    return () => clearInterval(interval);
  }, [food, gameOver]);

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "10px",
        padding: "8px",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "16px",
          fontSize: "13px",
          color: "var(--color-text-muted)",
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <span>
          <strong style={{ color: "var(--color-text)" }}>Score:</strong> {score}
        </span>
        {leaderboard && leaderboard.personalBest > 0 && (
          <span>
            <strong style={{ color: "var(--color-text)" }}>Best:</strong>{" "}
            {leaderboard.personalBest}
          </span>
        )}
        <span>Arrow keys / WASD while you wait</span>
      </div>
      <div
        style={{
          position: "relative",
          width: GRID_SIZE * CELL_SIZE,
          height: GRID_SIZE * CELL_SIZE,
          background: "var(--color-bg)",
          border: "1px solid var(--color-border)",
          borderRadius: "6px",
          overflow: "hidden",
        }}
      >
        {snake.map((s, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              left: s.x * CELL_SIZE,
              top: s.y * CELL_SIZE,
              width: CELL_SIZE - 1,
              height: CELL_SIZE - 1,
              background:
                i === 0 ? "var(--color-primary)" : "var(--color-primary-hover)",
              borderRadius: "2px",
              opacity: i === 0 ? 1 : 0.85,
            }}
          />
        ))}
        <div
          style={{
            position: "absolute",
            left: food.x * CELL_SIZE + 2,
            top: food.y * CELL_SIZE + 2,
            width: CELL_SIZE - 5,
            height: CELL_SIZE - 5,
            background: "var(--color-danger)",
            borderRadius: "50%",
          }}
        />
        {gameOver && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(15, 23, 42, 0.75)",
              color: "white",
              gap: "10px",
            }}
          >
            <div style={{ fontSize: "16px", fontWeight: 600 }}>Game Over</div>
            <div style={{ fontSize: "14px" }}>Score: {score}</div>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                reset();
              }}
            >
              Restart
            </button>
          </div>
        )}
      </div>
      {leaderboard && leaderboard.top.length > 0 && (
        <div
          style={{
            width: GRID_SIZE * CELL_SIZE,
            fontSize: "12px",
            color: "var(--color-text-muted)",
          }}
        >
          <div
            style={{
              fontWeight: 600,
              color: "var(--color-text)",
              marginBottom: "4px",
            }}
          >
            Leaderboard
          </div>
          <ol
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "flex",
              flexDirection: "column",
              gap: "2px",
            }}
          >
            {leaderboard.top.map((entry, i) => {
              const isMe =
                leaderboard.currentUserEmail === entry.user_email;
              return (
                <li
                  key={entry.user_email}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "2px 6px",
                    borderRadius: "3px",
                    background: isMe
                      ? "var(--color-primary-bg, rgba(99,102,241,0.12))"
                      : "transparent",
                    color: isMe ? "var(--color-text)" : undefined,
                    fontWeight: isMe ? 600 : 400,
                  }}
                >
                  <span>
                    {i + 1}. {displayName(entry.user_email)}
                    {isMe ? " (you)" : ""}
                  </span>
                  <span>{entry.best_score}</span>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}
